import React, { useCallback } from 'react'
import { useRouter } from 'next/router'
import { Button } from '@/atoms/Button'
import { ResponsiveFlexRow } from '@/atoms/FlexContainers'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { HeadingSM, ParagraphMD } from '@/atoms/Text'
import { Modal } from '@/molecules/Modal'
import { useUser } from '@/services/UserService'
import { mergeQueryParameters } from '@/utils/UriBuilder'
import { useTranslate } from '@/utils/translate/translate-client'

interface ContactSupportModalProps {
  open: boolean
  onClose: () => void
}

export const createZendeskLink = ({ email }: { email: string }) => {
  const baseParams = {
    ticket_form_id: '6742195490455',
    tf_anonymous_requester_email: email ?? '',
    tf_6742404389015: 'computer_web_page_field',
    tf_subject: 'Giveaway Issue (web)',
  }
  return mergeQueryParameters('https://support.angel.com/hc/requests/new', {
    ...baseParams,
  })
}

export const GiveawaySupportModal = ({ open, onClose }: ContactSupportModalProps) => {
  const { t } = useTranslate('tickets')
  const { reload, push } = useRouter()
  const { user } = useUser()

  const handleRetry = useCallback(() => {
    reload()
    onClose()
  }, [reload, onClose])

  const onContactSupportClick = useCallback(() => {
    return push(createZendeskLink({ email: user?.email ?? '' }))
  }, [push, user?.email])

  return (
    <Modal
      backgroundScreenColor="black"
      isOpen={open}
      onClose={onClose}
      panelClasses="!bg-white !max-w-[361px] !w-full md:!max-w-[600px] border-2 border-solid border-core-gray-900 !p-8 !pt-10 md:!p-16 relative"
      transition="appear"
      className="z-50 text-black "
    >
      <div>
        <div className="absolute right-6 top-6 cursor-pointer rounded-full bg-core-gray-300 p-2" onClick={onClose}>
          <CloseIcon color="black" size={16} />
        </div>
        <div className="flex flex-col gap-4">
          <HeadingSM className="md:!photon-heading-md" weight="bold">
            {t('sorry', 'Sorry!')}
          </HeadingSM>
          <ParagraphMD color="core-gray-700" className="md:!photon-paragraph-lg">
            {t(
              'contactSupportMessage',
              'An unexpected error has occurred on our end and we’re working to fix the issue. Please retry again later. If the problem persists, please contact customer support. ',
            )}
          </ParagraphMD>
        </div>
        <ResponsiveFlexRow breakPoint="md" className="mt-10 flex flex-col gap-2 md:gap-3">
          <Button variant="black" onClick={handleRetry} className="px-[28px] py-3">
            {t('retry', 'Retry')}
          </Button>
          <Button
            className="w-full !bg-core-gray-300 px-[28px] py-3"
            onClick={onContactSupportClick}
            variant="lightgray"
          >
            {t('contactCustomerSupport', 'Contact Customer Support')}
          </Button>
        </ResponsiveFlexRow>
      </div>
    </Modal>
  )
}
